"use client";

import React from "react";
import { Select, SelectItem } from "@heroui/react";
import { FilterSelectProps } from "./types";
import { SortArrowDown, SortArrowUp } from "../icons";

type Selection = 'all' | Set<React.Key>;

export type { FilterOption } from "./types";

const FilterSelect: React.FC<FilterSelectProps> = ({
  placeholder,
  options,
  selectedKey,
  onSelectionChange,
  startContent,
  endContent,
  className = "",
  disabled = false,
  size = "sm",
  variant = "default",
  autoWidth = false,
  sortDirection = null,
  onSortDirectionChange,
  ariaLabel,
  showPlaceholderWithValue = false,
}) => {

  const isPlaceholderSelected = React.useMemo(() => {
    if (!options || options.length === 0) {
      return true;
    }
    if (!selectedKey) {
      return true;
    }
    // Для showPlaceholderWithValue режима, 'all' не считается placeholder
    if (showPlaceholderWithValue && selectedKey === 'all') {
      return false;
    }
    return selectedKey === options[0].key;
  }, [options, selectedKey, showPlaceholderWithValue]);

  const getSelectedOptionWithIcon = () => {
    const selectedOption = options.find(option => option.key === selectedKey);
    if (!selectedOption) return placeholder;
    
    // Проверяем, является ли это сортировочным фильтром
    const isSortFilter = options.some(option => 
      option.key !== 'all' && (option.key.startsWith('-') || option.key.includes('created_at') || option.key.includes('arpu') || option.key.includes('romi'))
    );
    
    const getSortDirection = (key: string) => {
      if (key === 'all' || !isSortFilter) return null;
      if (key.startsWith('-')) return 'desc';
      return 'asc';
    };
    
    const direction = getSortDirection(selectedOption.key);
    
    // Если включен режим showPlaceholderWithValue и выбран конкретный элемент (не placeholder)
    if (showPlaceholderWithValue && !isPlaceholderSelected) {
      return (
        <div className="flex items-center gap-1">
          <span className="text-[#71717A]">{placeholder}:</span>
          <span className="text-[#11181C]">{selectedOption.label}</span>
        </div>
      );
    }
    
    if (direction && selectedOption.key !== 'all' && isSortFilter) {
      return (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            {selectedOption.icon && (
              <span className="flex-shrink-0">
                {selectedOption.icon}
              </span>
            )}
            <span>{selectedOption.label}</span>
          </div>
          <span className="ml-2">
            {direction === 'asc' ? '↑' : '↓'}
          </span>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-2">
        {selectedOption.icon && (
          <span>
            {selectedOption.icon}
          </span>
        )}
        <span>{selectedOption.label}</span>
      </div>
    );
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'campaign':
        return {
          trigger: "bg-[#f4f4f5] border-none rounded-[8px] !px-[12px] !min-h-[36px] !h-[36px]",
          value: "text-[14px]"
        };
      default:
        return {
          trigger: "h-9 bg-gray-100 border-none rounded-[8px] !px-[12px] hover:bg-gray-200 focus:border-blue-500",
          value: "text-[14px]"
        };
    }
  };

  const variantStyles = getVariantStyles();
  const valueColorClass = isPlaceholderSelected ? '!text-[#71717a]' : '!text-black';
  const hasSelectedInOptions = React.useMemo(
    () => options.some(option => option.key === selectedKey),
    [options, selectedKey]
  );
  const resolvedSelectedKeys: Selection = hasSelectedInOptions && selectedKey
    ? new Set<React.Key>([selectedKey])
    : new Set<React.Key>();
  
  return (
    <div 
      className="w-fit filter-select-wrapper" 
      style={{ 
        textOverflow: 'clip',
        whiteSpace: 'nowrap',
        overflow: 'visible'
      }}
    >
      <Select
        placeholder={placeholder}
        aria-label={ariaLabel || placeholder}
        selectedKeys={resolvedSelectedKeys}
        onSelectionChange={(keys: Selection) => {
          if (keys === 'all') {
            return;
          }
          const selected = Array.from(keys)[0] as string;
          onSelectionChange(selected);
        }}
        selectorIcon={<span aria-hidden className="hidden" />}
        renderValue={() => getSelectedOptionWithIcon()}
        startContent={startContent}
        disabled={disabled}
        size={size}
        style={{
          minWidth: 'fit-content',
          width: 'fit-content',
          textOverflow: 'clip'
        }}
        classNames={{
          base: "w-fit min-w-fit",
          trigger: `${variantStyles.trigger} ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          } w-fit min-w-fit whitespace-nowrap ${className}`,
          value: `${variantStyles.value} ${valueColorClass} whitespace-nowrap min-w-fit overflow-visible`,
          popoverContent: "rounded-xl w-fit max-w-[420px]",
          listbox: "p-[8px]",
          listboxWrapper: " max-w-[420px]",
          innerWrapper:'gap-[4px] min-w-fit flex-nowrap',
        }}
        popoverProps={{
          classNames: {
            content: "w-fit min-w-[226px] max-w-[420px] p-[0px]"
            
          }
        }}
      >
        {options.map((option) => {
          // Проверяем, является ли это сортировочным фильтром
          const isSortFilter = options.some(opt => 
            opt.key !== 'all' && (opt.key.startsWith('-') || opt.key.includes('created_at') || opt.key.includes('arpu') || opt.key.includes('romi'))
          );
          
          const getSortDirection = () => {
            if (option.key === 'all' || !isSortFilter) return null;
            if (option.key.startsWith('-')) return 'desc';
            return 'asc';
          };
          const direction = getSortDirection();
          const selectedIconProp = direction && option.key !== 'all' && isSortFilter
            ? { selectedIcon: direction === 'asc' ? <SortArrowUp/> : <SortArrowDown/> }
            : {};

          return (
            <SelectItem
              key={option.key}
              textValue={option.label}
              {...selectedIconProp}
              classNames={{
                base: "!p-[8px] !h-[32px] whitespace-nowrap !w-full !data-[hover=true]:bg-[#EEEEEF]"
              }}
            >
              <div className="flex items-center gap-[8px]">
                {option.icon && (
                  <span className="flex-shrink-0">
                    {option.icon}
                  </span>
                )}
                <span>{option.label}</span>
              </div>
            </SelectItem>
          );
        })}
      </Select>
    </div>
  );
};

export default FilterSelect;
